/**
 * Default/mock translations for development and fallback.
 * These will be replaced by the real translation service in production.
 * Keys match the translationKeys.ts values.
 */
export const defaultTranslations: Record<string, string> = {
  // Portfolio Statement / Download Asset Statement Modal
  "portfolio-statement.button.label": "Download Statement",
  "portfolio-statement.modal.title": "Download Asset Statement",
  "portfolio-statement.select-all": "Select All",
  "portfolio-statement.deselect-all": "Deselect All",
  "portfolio-statement.selected-count": "{selected} of {total} assets selected",
  "portfolio-statement.generate.button": "Generate Statement",
  "portfolio-statement.order-info":
    "Assets in the PDF will be ordered by total issuer position value.",
  "portfolio-statement.download.success":
    "Your Asset Statement download will start shortly",
  "portfolio-statement.download.error":
    "Something went wrong while generating your PDF. Please try again.",
  "portfolio-statement.loading-assets": "Loading assets...",
  "portfolio-statement.no-assets":
    "No assets with available statements were found.",
  "portfolio-statement.assets.error":
    "Failed to load assets. Please try again later.",

  // Date Range Section
  "portfolio-statement.date-range.title": "Date Range",
  "portfolio-statement.date-range.subtitle":
    "Select the date range for the asset statement of {tokenName}",
  "portfolio-statement.starting-date": "Starting Date",
  "portfolio-statement.ending-date": "Ending Date",

  // Included Sections
  "portfolio-statement.included-sections.title": "Included Sections",
  "portfolio-statement.included-sections.subtitle":
    "Select the sections you'd like to include in your Asset Statement",
  "portfolio-statement.section.investment-overview": "Investment Overview",
  "portfolio-statement.section.performance-overview": "Performance Overview",
  "portfolio-statement.section.custody-overview": "Custody Overview",
  "portfolio-statement.section.transaction-history": "Transaction History",
  "portfolio-statement.section.payout-history": "Payout History",

  // Included Tokens
  "portfolio-statement.included-tokens.title": "Included Tokens",
  "portfolio-statement.included-tokens.subtitle":
    "Select the tokens you'd like to include in your Asset Statement",

  // Download PDF Button
  "portfolio-statement.download-pdf": "Download PDF",

  // Error handling
  "portfolio-statement.try-again": "Try Again",
  "portfolio-statement.no-statements-available":
    "No statements are available for the selected assets.",
  "portfolio-statement.rate-limited":
    "Too many requests. Please wait a moment and try again.",
  "portfolio-statement.generating":
    "Your statement is being generated. Please wait...",
  "portfolio-statement.timeout":
    "Statement generation timed out. Please try again later.",
  "portfolio-statement.unauthorized":
    "Your session has expired. Please log in again.",
  "portfolio-statement.forbidden":
    "You do not have permission to generate this statement.",
  "portfolio-statement.invalid-request":
    "The request could not be processed. Please review your selections.",
  "portfolio-statement.server-error":
    "An unexpected server error occurred. Please try again later.",

  // Transactions Pending Actions Alert
  "transactions-pending-actions-alert.title":
    "{amount} transaction(s) have pending actions.",
  "transactions-pending-actions-alert.subtitle":
    "Some transactions in your history have pending actions. Click 'View records' to review all transactions that require your attention.",
  "transactions-pending-actions-alert.cta": "View records",

  // Claim Redemption Modal
  "claim-redemption-modal.title": "Claim Redemption for {asset}",
  "claim-redemption-modal.description":
    "You're about to claim your redemption for {asset}. Please review the details and <strong>connect the claimable wallet shown below</strong> to sign the transaction.",
  "claim-redemption-modal.connected-wallet-label": "Connected Wallet",
  "claim-redemption-modal.claimable-wallet-label": "Claimable Wallet",
  "claim-redemption-modal.shares-redeemed-label": "Shares Redeemed",
  "claim-redemption-modal.units-not-redeemed-label": "Units not redeemed",
  "claim-redemption-modal.usdc-to-claim-label": "USDC to claim",
  "claim-redemption-modal.shares-unit": "shares",
  "claim-redemption-modal.connect-wallet-button": "Connect wallet",
  "claim-redemption-modal.cancel-button": "Cancel",
  "claim-redemption-modal.sign-transaction-button": "Sign Transaction",
  "claim-redemption-modal.error-snackbar": "An error has occurred, contact",
  "claim-redemption-modal.success-snackbar":
    "Redemption claimed successfully. Your USDC will be released to your wallet shortly.",
  "claim-redemption-modal.wallet-copied": "Wallet address copied",
  "claim-redemption-modal.wrong-wallet-title": "Wrong wallet connected",
  "claim-redemption-modal.wrong-wallet-description":
    "The connected wallet does not match the claimable wallet for this redemption. Click on the connected wallet address to disconnect, then connect {wallet} to proceed.",
  "claim-redemption-modal.prorata-callout-title":
    "Not all units could be redeemed",
  "claim-redemption-modal.prorata-callout-description":
    "The fund reached its redemption cap for this window. {units} units come back to your wallet with this claim — the USDC and the units are sent together — and you can redeem them again in a future window.",

  // Tax Form Expiring Modal
  "tax-form-expiring-modal.title": "Your tax form is expiring soon",
  "tax-form-expiring-modal.message":
    "Your <strong>{formType}</strong> expires on <strong>{expirationDate}</strong> (in {daysUntilExpiration} days). Renewit now to keep receiving distributions without interruption.",
  "tax-form-expiring-modal.renew-button": "Renew my {formType}",
  "tax-form-expiring-modal.remind-later-button": "Remind me later",

  // Tax Form Expired Modal
  "tax-form-expired-modal.title": "Your tax form has expired",
  "tax-form-expired-modal.message":
    "Your <strong>{formType}</strong> expired on <strong>{expirationDate}</strong>. Until you submit a new one, your distributions are subject to <strong>the full 30% withholding rate</strong>.",
  "tax-form-expired-modal.renew-button": "Complete new {formType}",
  "tax-form-expired-modal.remind-later-button": "Remind me later",

  // Tax Form Invalid Modal
  "tax-form-invalid-modal.title": "Your tax form is no longer valid",
  "tax-form-invalid-modal.message-w9":
    "Your <strong>{formType}</strong> submitted on <strong>{submissionDate}</strong> is no longer valid. Until you submit a new one, your distributions are subject to <strong>24% backup withholding</strong>.",
  "tax-form-invalid-modal.message-w8":
    "Your <strong>{formType}</strong> submitted on <strong>{submissionDate}</strong> is no longer valid. Until you submit a new one, your distributions are subject to <strong>the full 30% withholding rate</strong>.",
  "tax-form-invalid-modal.reason-title": "Why it is no longer valid",
  "tax-form-invalid-modal.renew-button": "Complete new {formType}",
  "tax-form-invalid-modal.remind-later-button": "Remind me later",

  // Cancel Redemption Modal
  "cancel-redemption-modal.title": "Cancel redemption · {ticker}",
  "cancel-redemption-modal.description":
    "Cancelling ends this open redemption request on {ticker}. Your {units} {ticker} stay in your position and no USDC is sent. The whole request is cancelled — you cannot cancel part of it.",
  "cancel-redemption-modal.connected-wallet-label": "Connected Wallet",
  "cancel-redemption-modal.signing-wallet-label": "Signing Wallet",
  "cancel-redemption-modal.units-released-label": "Units released",
  "cancel-redemption-modal.acknowledgement":
    "I have read the above and understand that cancelling cannot be undone.",
  "cancel-redemption-modal.keep-button": "Keep redemption",
  "cancel-redemption-modal.cancel-button": "Cancel redemption",
  "cancel-redemption-modal.wallet-copied": "Wallet address copied",
  "cancel-redemption-modal.success-snackbar":
    "Redemption cancelled successfully. Your units stay in your position.",
  "cancel-redemption-modal.wrong-wallet-description":
    "The connected wallet does not match the signing wallet for this redemption. Click on the connected wallet address to disconnect, then connect {wallet} to proceed.",

  // Open Orders Widget
  "open-orders-widget.title": "Open Orders",
  "open-orders-widget.column-type": "Type",
  "open-orders-widget.column-status": "Status",
  "open-orders-widget.column-units": "Units",
  "open-orders-widget.column-price": "Price",
  "open-orders-widget.column-total-value": "Total Value",
  "open-orders-widget.column-wallet": "Wallet",
  "open-orders-widget.type-subscription": "Subscription",
  "open-orders-widget.type-redemption": "Redemption",
  "open-orders-widget.status-processing": "Processing",
  "open-orders-widget.status-usdc-available": "USDC available",
  "open-orders-widget.status-processing-tooltip":
    "Your order is being processed.",
  "open-orders-widget.status-canceled": "Canceled",
  "open-orders-widget.status-claimed": "Claimed",
  "open-orders-widget.cancel-redemption": "Cancel redemption",
  "open-orders-widget.claim-usdc": "Claim USDC",
  "open-orders-widget.empty": "You have no open orders.",
};
